import { Link2Off, Pencil } from "lucide-react";

import { Button } from "@/components/ui";
import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview";
import { useFieldUsers } from "@/hooks/useFieldUsers";
import { usePathLabels } from "@/hooks/usePathLabels";
import { pickTarget } from "@/lib/attributeReview/pickTarget";
import type { PickWhyView } from "@/lib/attributeReview/pickWhy";
import type { AgentAnswers } from "@/lib/attributeReview/reviewRow.types";
import type { StepNoteData } from "@/lib/attributeReview/stepOne";
import type { Mark } from "@/lib/attributeReview/stepMarks";
import { evidenceLines } from "@/lib/attributeReview/evidenceLines";
import { pathLabel } from "@/lib/attributeReview/pathLabel";
import { usedBySummary } from "@/lib/attributeReview/usedBySummary";
import { formatProductType } from "@/lib/formatProductType";
import { noFieldNote, noFieldTarget } from "@/lib/attributeReview/noFieldPick";

import type { useRowDecision } from "../AttributeReviewRow/useRowDecision";

import { DecisionCard, type CardTone, type Headline } from "./DecisionCard";
import { EvidenceList } from "./EvidenceList";
import { PickWhy } from "./PickWhy";
import { ReviewDrawerAgentPanel } from "./ReviewDrawerAgentPanel";
import { ChangePick } from "./ChangePick";
import { ReviewStep } from "./ReviewStep";
import { StepNote } from "./StepNote";
import { UsedByBox } from "./UsedByBox";

type Props = {
  row: AttributeReviewMappingRow;
  pickedPath: string | null;
  pickedLeaf: string | null;
  noFieldKind: string | null;
  marketplace: string;
  mark: Mark;
  status: string;
  proof: string;
  notes: StepNoteData[];
  readOnly: boolean;
  linked: boolean;
  linkMode: boolean;
  fieldLabelOf: (id: string) => string;
  decision: ReturnType<typeof useRowDecision>;
  onCopy?: (copyOf: string) => void;
  onUnlink: () => void;
  onEdit: () => void;
  onIgnoreCopy: () => void;
  locked: boolean;
  sessionId: string;
  nameFocus: number;
  why: PickWhyView | null;
};

export function DecisionStep(p: Props) {
  const { row, readOnly, pickedPath, pickedLeaf } = p;
  const users = useFieldUsers(pickedPath);
  const labelOf = usePathLabels(Boolean(pickedPath));
  const agent = row.agent as AgentAnswers | null;
  const store = formatProductType(p.marketplace);
  const note = p.noFieldKind && noFieldNote(p.noFieldKind);
  const field = String(row.sourceAttribute ?? row.id);
  const hasPick = Boolean(pickedPath || pickedLeaf || p.noFieldKind);
  const { pickLabel, toName } = pickTarget(
    row,
    pickedPath,
    pickedLeaf,
    labelOf,
  );
  const headline: Headline = readOnly
    ? {
        text: `${field} is saved as `,
        target: p.noFieldKind ? noFieldTarget(p.noFieldKind, store) : pickLabel,
        tail: ".",
      }
    : !hasPick
      ? { text: `Which SmartStore field does ${field} fill?` }
      : p.noFieldKind
        ? {
            text: `Save ${store} “${field}” as `,
            target: noFieldTarget(p.noFieldKind, store),
            tail: "?",
          }
        : p.linkMode
          ? { text: `Link ${store} “${field}” to `, target: toName, tail: "?" }
          : {
              text: `Save ${store} “${field}” as `,
              target: `“${pickLabel}”`,
              tail: "?",
            };
  const cardTone: CardTone = readOnly
    ? "info"
    : !hasPick
      ? "none"
      : p.mark === "done"
        ? "good"
        : "warn";
  return (
    <ReviewStep n={1} mark={p.mark} title="Field" status={p.status}>
      <DecisionCard
        tone={cardTone}
        headline={headline}
        from={{ store, field }}
        to={pickedPath ? { label: pickLabel, path: pickedPath } : null}
        usedBy={
          pickedPath && (
            <UsedByBox
              summary={usedBySummary(users.users, p.marketplace)}
              loading={users.loading}
              failed={users.failed}
              onRetry={users.retry}
              fieldLabel={field}
              pickLabel={pickLabel}
            />
          )
        }
        afterHeadline={
          <>
            {p.notes.map((n) => (
              <StepNote
                key={n.title}
                tone="warn"
                title={n.title}
                body={n.body}
              />
            ))}
            <PickWhy
              why={p.why}
              readOnly={readOnly}
              canIgnore={!readOnly && !p.locked}
              onIgnoreCopy={p.onIgnoreCopy}
            />
            <ReviewDrawerAgentPanel
              row={row}
              marketplace={p.marketplace}
              pickedPath={pickedPath}
              pickedKind={p.noFieldKind}
              setPath={readOnly ? undefined : p.decision.setPath}
              fieldLabelOf={p.fieldLabelOf}
              onCopy={p.onCopy}
            />
          </>
        }
      >
        {note && (
          <p className="text-[13px] text-slate-600 dark:text-slate-300">
            {note}
          </p>
        )}
        <EvidenceList
          lines={evidenceLines({
            agent,
            pickedPath,
            proof: p.proof,
            otherLabel: (a) =>
              a.path
                ? pathLabel(labelOf, a.path)
                : a.copy
                  ? `a copy of ${p.fieldLabelOf(a.copy)}`
                  : "no field",
          })}
        />
        {readOnly && (
          <div className="flex gap-2">
            {p.linked && (
              <Button
                size="sm"
                variant="outline"
                disabled={p.locked}
                onClick={p.onUnlink}
              >
                <Link2Off className="h-4 w-4" /> Unlink
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={p.locked}
              onClick={p.onEdit}
            >
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </div>
        )}
        {!readOnly && (
          <ChangePick
            mapping={row}
            decision={p.decision}
            pickedPath={pickedPath}
            sessionId={p.sessionId}
            store={store}
            marketplace={p.marketplace}
            focusKey={p.nameFocus}
          />
        )}
      </DecisionCard>
    </ReviewStep>
  );
}
